import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { hexOk } from '../utils/contraste'
import api, { clinicaVisitada, clinicaDeSesion, suscribirVisita } from '../services/api'
import { ponerWhatsappBiango } from '../utils/whatsappBiango'
import { ponerNovedadesParaLasClinicas } from '../utils/novedadesDeVersion'
import { ponerFuncionesEncendidas } from '../utils/funcionesEstado'   // t279 · las funciones en desarrollo encendidas para esta clínica
import { ponerEdicionLocal } from '../utils/edicionLocalEstado'   // B-176 · la edición local (en la nube no viene: nada cambia)
import { ponerTipoNegocio } from '../utils/tipoNegocio'   // t524 · el tipo de negocio de la clínica (solo con su función encendida)

// LA MARCA QUE VE EL USUARIO (nombre y logo en cabeceras, login, pre-registro…) y las dos banderas
// que deciden cómo se comporta la app en ESTA clínica.
//
// DOS FUENTES, Y CADA UNA POR UN MOTIVO:
//   · /api/branding-publico — pantallas que se pintan ANTES de saber quién entra (login, portal del
//     paciente). El servidor resuelve la clínica por el DOMINIO; si no la reconoce, devuelve marca
//     NEUTRA. Aquí no puede haber ningún nombre escrito a fuego: `nombre: 'Clínica Dental Esteve'`
//     como valor por defecto hacía que CUALQUIER otra clínica viera el nombre de otra empresa en su
//     pantalla de acceso, incluso con el servidor respondiendo bien.
//   · /api/config-cliente — con sesión. `modulo_asistencias` no es estética: es un guardia de ruta que
//     enciende o apaga un módulo entero. Se responde por la clínica de quien pregunta, no por una
//     constante. (`standalone` sigue llegando, siempre true: sin el conector de Gesden ninguna pantalla
//     lo lee desde t606.)
//
// LA CACHÉ VA POR CLÍNICA. Con una sola clave global, una tablet compartida o un navegador que ha
// visto dos clínicas arrastraba la marca de la anterior — y en una pantalla de acceso eso se lee
// como que te has equivocado de sitio.
// El nombre por defecto es el del PRODUCTO, no el de un cliente. Antes era 'Clínica Dental Esteve' y
// eso ponía el nombre de otra empresa en la pantalla de acceso de cualquier otra clínica. Dejarlo en
// null tampoco vale: hay 31 sitios que lo pintan y saldrían en blanco.
// `correo_pacientes: false` de partida A PROPOSITO. Mientras no se sepa, no se ofrece un envio por
// correo: equivocarse hacia "si" pone el nombre de otra empresa como remitente de un documento de
// salud, y equivocarse hacia "no" solo deja a la vista WhatsApp y copiar el enlace, que es lo que la
// propia ayuda promete como alternativa. El servidor contesta en el mismo viaje que la marca.
// `cargando`: hay sesion en una clinica que el dominio no identifica y todavia no ha llegado su
// marca. Mientras vale true, la pantalla NO pinta el color de nadie — enseña que esta cargando.
const DEFECTO = { nombre: 'Biango', logo: null, color: null, color_secundario: null, standalone: false, modulo_asistencias: true, correo_pacientes: false, correo_motivo: null, correo_donde: null, modo_correccion: false, version: null, cargando: false }
// t667 · lo más que se espera a cada una de las dos preguntas de la marca (la del dominio y la de la sesión).
export const PLAZO_MARCA_MS = 12000
// La caché va por DOMINIO, que es exactamente lo que el servidor usa para resolver la clínica: una
// tablet compartida o un navegador que ha visto dos clínicas arrastraba la marca de la anterior, y en
// una pantalla de acceso eso se lee como que te has equivocado de sitio.
// …Y POR LA CLINICA EN LA QUE SE ESTA. Estando el soporte dentro de otra clínica, lo que se resuelve
// no es la marca del dominio sino la de ESA clínica: guardarla bajo la misma clave la dejaría pegada
// al salir, y la clínica propia se vería a sí misma con el nombre y el logo de un cliente.
const _visitaId = () => { const v = _visita(); return v ? v.id : null }
// …Y DESDE LA CUENTA UNICA, POR LA CLINICA DE LA SESION, NO SOLO POR LA DE LA VISITA. El mismo
// correo entra en varias clinicas por el MISMO dominio (el login enseña tarjetas): con la clave
// vieja, la sesion de la clinica B pintaba de golpe la marca cacheada de la A —nombre, logo y
// color— antes de preguntar nada, y ahi se quedaba. La clave lleva la clinica en la que se ESTA,
// que es la visita si la hay y la de la cuenta si no (services/api · clinicaDeSesion).
const _clinicaAhora = () => { try { return clinicaDeSesion() } catch (_) { return _visitaId() } }
const KEY = () => 'branding_cache:' + (typeof location !== 'undefined' ? location.host : 'app')
  + (_clinicaAhora() ? ':clinica:' + _clinicaAhora() : '')

let _memoria = null    // evita re-fetch entre montajes en la misma sesión
// CON QUÉ TOKEN se resolvió lo que hay en memoria. Sin esto, la pantalla de LOGIN —que también pinta
// la marca, y por tanto llama a este hook SIN sesión— dejaba cacheadas las banderas por defecto
// (`modulo_asistencias`, `modo_correccion`…), y como se entra con navigate() y no con una recarga,
// NADIE volvía a preguntar: la sesión entera seguía con ellas hasta que alguien pulsaba F5.
let _memoriaToken = null

// ── QUIÉN ESTÁ MIRANDO AHORA MISMO ───────────────────────────────────────────────────────────────
// Vaciar la caché no basta: los `useBranding` que ya están montados no se enteran de nada. Su efecto
// depende del token, y al guardar el color en Ajustes el token no cambia — así que nadie volvía a
// preguntar. El dueño elegía su color, pulsaba Guardar, leía «Datos de la clínica guardados» y LA
// APLICACIÓN NO CAMBIABA: ni al guardar, ni al navegar a otra pantalla. Solo recargando con F5.
// Es el fallo más tonto posible en la única función cuyo propósito entero es verse.
// Con esto, `olvidarBranding()` además AVISA, y cada pantalla viva vuelve a preguntar sola.
const _mirando = new Set();
let _version = 0;

// EL COLOR DE MARCA ES LO ÚNICO DE AQUÍ QUE ACABA DENTRO DE UNA PROPIEDAD CSS, así que se sanea en
// la puerta: seis dígitos hexadecimales o nada. El servidor ya lo valida al guardarlo, pero lo que
// llega aquí llega por la red y además se guarda en localStorage, donde puede quedarse días. Un
// valor como `red; background: url(https://…)` sería escribir CSS en la pantalla de todo el equipo
// y llamar a un servidor de fuera desde dentro de la app. Un color que no vale se trata como "esta
// clínica no tiene color": la app se queda con los colores de Biango, que es un estado normal.
// El SECUNDARIO (5-oct-2026) pasa por la misma puerta y con el mismo filtro: también acaba dentro de una
// propiedad CSS. Uno que no vale es «sin secundario» (se calcula del principal).
const _sanear = (v) => {
  if (!v) return v
  let o = v
  if (!hexOk(o.color)) o = { ...o, color: null }
  if (o.color_secundario != null && !hexOk(o.color_secundario)) o = { ...o, color_secundario: null }
  return o
}

// ── LA MARCA SIGUE A LA VISITA ───────────────────────────────────────────────────────────────────
//
//  EL AGUJERO, MEDIDO EJECUTANDO EL HOOK. La marca se resolvía SIEMPRE por el DOMINIO
//  (`/api/branding-publico`) y las banderas por un `fetch` CRUDO a `/api/config-cliente` que no
//  pasaba por el interceptor de `services/api` y por tanto NUNCA llevaba `X-Clinica-Id`. Con el
//  soporte dentro de la clínica 7, este hook contestaba `nombre: «Clínica del soporte»` y
//  `clinica_id: 90` — la clínica PROPIA— mientras la aplicación entera enseñaba y escribía los datos
//  del cliente. Y como este hook es la ÚNICA puerta por la que la interfaz sabe cómo se llama la
//  clínica, esa mentira salía por 30 y pico sitios a la vez: la cabecera del menú lateral (detrás
//  cuelgan pacientes, agenda, WhatsApp, caja, presupuestos, cirugías y almacén), el pie del panel de
//  administración, la cabecera del PDF de Informes, el historial impreso del paciente, el correo de
//  pedido de material y las etiquetas del almacén. Un documento con el membrete de una empresa y los
//  datos de otra no es un documento feo: es un documento falso.
//
//  Y NO ERA SOLO EL NOMBRE. `modulo_asistencias` es un guardia de ruta (App.jsx redirige si es
//  false) y `correo_pacientes` decide si sale el botón de enviar por correo Y CON QUÉ IDENTIDAD.
//  Los dos venían de la clínica del soporte mientras se trabajaba dentro de otra.
//
//  LA RAÍZ, Y POR QUÉ AQUÍ. No se arregla sitio por sitio —son treinta y pico y mañana son treinta y
//  siete—: se arregla en la única puerta. Dos piezas:
//   1) La configuración se pide con `api` (el axios de `services/api`), así que pasa por SU
//      interceptor y lleva la cabecera de la clínica visitada como cualquier otra petición de la
//      aplicación. Un `fetch` crudo con una copia del criterio sería una segunda puerta.
//   2) Con una visita abierta, la IDENTIDAD (nombre, logo, color) sale de esa misma respuesta —que
//      el servidor contesta por `req.clinicaId`, o sea por la clínica visitada— y no del dominio.
//      Sin visita no se lee ni uno de esos campos: la marca la sigue resolviendo el dominio, byte
//      por byte como antes, que es lo que ve cualquier usuario normal.
//  Y entrar o salir de una clínica AVISA: se entra con `navigate()`, no con una recarga, así que sin
//  esto la pantalla seguiría con el nombre anterior hasta que alguien pulsara F5.
const _visita = () => { try { return clinicaVisitada() } catch (_) { return null } }

// Una sola suscripción por proceso: cada pantalla montada ya se entera por `_mirando`.
let _atadoALaVisita = false
function _atarALaVisita() {
  if (_atadoALaVisita) return
  _atadoALaVisita = true
  try { suscribirVisita(() => { olvidarBranding() }) } catch (_) { /* sin visitas no hay nada que atar */ }
}

// Lo último que se supo, para pintar sin parpadeo. Se busca por la clínica de la sesión si la hay.
function _deCache() {
  try {
    const v = JSON.parse(localStorage.getItem(KEY()) || 'null')
    return v ? _sanear({ ...DEFECTO, ...v }) : DEFECTO
  } catch { return DEFECTO }
}

const _token = () => { try { return localStorage.getItem('token') || null } catch { return null } }

// ── EL AVISO A LAS PANTALLAS QUE YA ESTAN MONTADAS ───────────────────────────────────────────────
//
//  UNA SOLA MITAD NO BASTA, Y ESTO SE MIDIO EJECUTANDO. `main.jsx` vacía la caché de react-query al
//  cambiar de clínica, y su comentario prometía que «lo que siga montado vuelve a preguntar solo».
//  Es FALSO: con el `QueryObserver` de verdad, `clear()` vacía la caché pero el observador CONSERVA
//  su último resultado y NO vuelve a pedir nada hasta que React lo repinta. O sea que la vista previa
//  EN VIVO de un presupuesto y de una factura (Pacientes.jsx, DocPreview) y las dos tarjetas de
//  Ajustes seguían pintando la marca de la clínica ANTERIOR después del cambio.
//
//  Esta es la otra mitad: cuando `olvidarBranding()` avisa, la pantalla vuelve a pintar,
//  `claveDeMarca()` se vuelve a evaluar —ya con la clínica nueva— y react-query sí va al servidor.
//  La usan LOS DOS hooks de esta puerta, que es lo que impide que uno se entere y el otro no.
//
//  Y EL CAMBIO DE VISITA NO SIEMPRE VIENE DE UN `navigate()`: llega también cuando la clínica CORTA
//  el acceso (403 → `terminarVisita`) y desde OTRA PESTAÑA (evento `storage`). Los tres caminos
//  pasan por `suscribirVisita`, que es a lo que se ata `_atarALaVisita()`.
function useAvisoDeMarca() {
  const [version, setVersion] = useState(_version)
  useEffect(() => {
    const avisar = () => setVersion(_version)
    _mirando.add(avisar)
    // Entrar en otra clínica (o salir) cambia la marca y las banderas. Se entra con `navigate()`,
    // no recargando: sin este aviso la pantalla se quedaría con el nombre de la clínica anterior.
    _atarALaVisita()
    return () => { _mirando.delete(avisar) }
  }, [])
  return version
}

export function useBranding() {
  // EL PRIMER PINTADO, ANTES DE PEDIR NADA. Si hay sesion y no hay nada cacheado DE SU CLINICA, se
  // arranca en «cargando» en vez de con la marca de fabrica: asi no se ve el color de Biango dar
  // paso al de la clinica. Con cache (la segunda vez que entra) sale su color al instante.
  const [branding, setBranding] = useState(() => {
    const v = _memoria || _deCache()
    if (v && v.color) return v
    const hayToken = !!_token()
    return hayToken ? { ...v, cargando: true } : v
  })
  // El token, como estado: al entrar cambia de null a algo y este efecto vuelve a correr.
  const token = _token()
  // La versión sube cuando alguien llama a olvidarBranding(). Al ser dependencia del efecto de
  // abajo, ese aviso hace que esta pantalla vuelva a preguntar sin necesidad de recargar.
  const version = useAvisoDeMarca()

  useEffect(() => {
    // Se re-pregunta cuando cambia QUIÉN pregunta, no solo la primera vez.
    if (_memoria && _memoriaToken === token) { setBranding(_memoria); return }
    let vivo = true
    // `cachear` contesta a UNA pregunta: ¿se ha podido CONFIRMAR de quién es esta identidad? Si no,
    // se pinta lo menos malo y NO se guarda en ninguna parte. Guardar una identidad sin confirmar
    // bajo la clave de la clínica visitada es como la marca del soporte se quedaba clavada TODA la
    // sesión —y sobrevivía a un F5— haciéndose pasar por la del cliente.
    const guardar = (bruto, cachear = true) => {
      if (!vivo) return
      const v = _sanear(bruto)
      if (cachear) {
        _memoria = v
        _memoriaToken = token
        try { localStorage.setItem(KEY(), JSON.stringify(v)) } catch {}
      }
      setBranding(v)
    }
    // 1) La identidad, siempre (funciona sin sesión).
    // ── t667 · UNA RESPUESTA FALLIDA NO PUEDE DEJAR LA PANTALLA DE CARGA PARA SIEMPRE ───────────
    //  El dueño, 3-nov: «al entrar a la clínica se queda en Biango y no carga». Medido con la app de verdad en un navegador:
    //  si esta petición fallaba UNA vez (un 502 mientras el servidor se reinicia, una página de error en vez de JSON, un corte
    //  de red), aquí había un `if (!b) return` y un `.catch(() => {})` mudo: no se preguntaba nada más y `cargando` se quedaba
    //  en true, así que App.jsx (PrivateRoute) enseñaba «Biango · Cargando» SIN FIN, con cualquier tipo de clínica. Ahora un
    //  fallo aquí es «el dominio no ha dicho nada»: se sigue con la pregunta de la sesión (que sí sabe de quién es la pantalla)
    //  y la espera se apaga pase lo que pase. Y con un plazo: una petición colgada tampoco puede dejarla esperando.
    let cortar = null
    let relojCorte = null
    try {
      if (typeof AbortController === 'function') {
        const c = new AbortController()
        cortar = c
        relojCorte = setTimeout(() => { try { c.abort() } catch (_) { /* ya contestó */ } }, PLAZO_MARCA_MS)
      }
    } catch (_) { cortar = null }
    fetch('/api/branding-publico', cortar ? { signal: cortar.signal } : undefined)
      .then(r => r.ok ? r.json() : null)
      .catch(() => null)
      .then(async (b) => {
        if (relojCorte) { try { clearTimeout(relojCorte) } catch (_) { /* nada que parar */ } }
        // Lo que venga null del servidor NO pisa el valor por defecto: `{...DEFECTO, ...b}` con
        // `b.nombre === null` dejaría el nombre en null y la cabecera en blanco.
        const delDominioSabido = !!(b && typeof b === 'object')
        let v = { ...DEFECTO, ...Object.fromEntries(Object.entries(delDominioSabido ? b : {}).filter(([, x]) => x != null)) }
        // QUÉ CLÍNICA RESOLVIÓ EL DOMINIO. Se guarda antes de tocar nada: abajo hace falta para saber
        // si el servidor está contestando por esa misma clínica (o sea, si la marca del dominio es la
        // de esta sesión) o por otra.
        const delDominio = v.clinica_id != null ? parseInt(v.clinica_id) : null
        // EN QUÉ CLÍNICA SE ESTÁ, leído ANTES de preguntar: hace falta también en el camino del
        // fallo, y ahí no hay ninguna respuesta de la que sacarlo.
        const dentro = _visita()
        // EN QUE CLINICA SE ESTA (visita, o la de la cuenta). Ver services/api · clinicaDeSesion.
        const enQue = _clinicaAhora()
        // ── EL PARPADEO: PINTAR LA MARCA DEL DOMINIO Y CORREGIRLA UN SEGUNDO DESPUES ───────────
        //  Lo dijo el dueño el 2-sep: «el sidebar debe tener al momento el color de la clinica,
        //  actualmente sale con el color de biango y al poco se cambia». Es esta linea: la marca del
        //  DOMINIO llega primero (una peticion sin sesion, rapida) y la de la clinica de la sesion
        //  llega despues. Con la cuenta unica el dominio ya no es la clinica, asi que ese primer
        //  pintado no es «casi lo correcto»: es OTRA empresa, y encima se ve cambiar.
        //  Estando dentro de una clinica que NO es la del dominio, no se pinta nada suyo: se espera
        //  a la respuesta buena marcando `cargando`, que es lo que la pantalla usa para enseñar el
        //  logo mientras tanto. En su propio dominio (el caso de siempre) no cambia nada.
        const _sesion = enQue
        // MULTI-TENANT DE VERDAD: **TODAS las clinicas entran por el MISMO dominio** (hoy
        // app.clinicaesteve.es, mañana biango.app). Lo aclaro el dueño el 2-sep. Asi que el dominio
        // NO identifica a nadie: o no resuelve clinica (`delDominio` null) o resuelve SIEMPRE la
        // misma, y para todas las demas su marca es la de otra empresa. Por eso la condicion no es
        // «la sesion difiere del dominio» sino «el dominio no me ha dado LA MIA»: con sesion y sin
        // esa certeza, se espera. Quien entre en la clinica del dominio no espera nada, igual que hoy.
        const _otra = !!token && (delDominio == null || _sesion == null || _sesion !== delDominio)
        if (_otra) v = { ...v, nombre: DEFECTO.nombre, logo: null, color: null, color_secundario: null, cargando: true }
        // ¿SE HA PODIDO CONFIRMAR DE QUIÉN ES ESTA IDENTIDAD?
        //   · SIN SESION (login, portal): la del dominio es la unica respuesta posible, y es la buena.
        //   · CON SESION EN LA CLINICA DEL DOMINIO: igual que siempre — el caso de toda clinica que
        //     entra por su propio dominio, que no puede cambiar en nada.
        //   · CON SESION EN OTRA (cuenta unica: el mismo correo en varias clinicas, o una visita):
        //     el dominio NO la identifica. Hay que preguntar, y hasta que el servidor conteste no se
        //     da por buena ninguna marca — fallar hacia la del dominio es servir la de otra empresa.
        //   · Y SIN SABER en cual se esta (sesion abierta antes de que el login sirviera `clinica_id`): se
//     da por buena la del dominio, que es como se comportaba la aplicacion antes de la cuenta unica.
//     No hay evidencia de que no sea la suya, y degradar a ciegas dejaria SIN NOMBRE la pantalla de
//     toda clinica normal ante un hipo de red. La mentira solo la puede destapar el servidor, y si
//     ha contestado, abajo se corrige.
        // t667 · sin respuesta del dominio no hay nada suyo que confirmar: solo la del servidor (abajo) puede darla por buena.
        let confirmada = delDominioSabido && (!token || (!dentro && (enQue == null || (delDominio != null && enQue === delDominio))))
        // 2) Las banderas, solo con sesión. Si falla —o no hay sesión— se quedan los valores de
        // siempre: modo Gesden y módulo visible, que es como se comportaba la app antes de esto.
        try {
          const t = token
          if (t) {
            // POR `api`, NO POR UN `fetch` CRUDO: es lo que hace que esta pregunta lleve la cabecera
            // de la clínica visitada como cualquier otra de la aplicación. Ver el bloque de arriba.
            // t667 · con plazo: una respuesta que no llega no puede tener la pantalla de carga puesta dos minutos.
            const c = (await api.get('/config-cliente', { timeout: PLAZO_MARCA_MS })).data || {}
            // 3-oct-2026 · «Escribir por WhatsApp» en el «?»: solo si el servidor lo ha encendido (utils/whatsappBiango).
            ponerWhatsappBiango(c.biango_whatsapp)
            // 7-oct-2026 · ¿se abren a las clínicas las novedades de cada versión? Hoy no (utils/novedadesDeVersion).
            ponerNovedadesParaLasClinicas(c.novedades_para_clinicas)
            // t279 · qué funciones en desarrollo están encendidas para esta clínica (utils/funcionesEstado): sin la lista, ninguna.
            ponerFuncionesEncendidas(c.funciones)
            // B-176 · solo en la edición local: lo que no está en ella y el estado de su licencia (utils/edicionLocalEstado).
            ponerEdicionLocal(c.edicion_local)
            // t524 · B-2260 · el tipo de negocio (utils/tipoNegocio): sin el campo (un servidor anterior a t667), dental, como siempre.
            ponerTipoNegocio(c.tipo_negocio)
            v = { ...v, standalone: c.standalone === true, modulo_asistencias: c.modulo_asistencias !== false, clinica_id: c.clinica_id ?? v.clinica_id,
              correo_pacientes: c.correo_pacientes === true, correo_motivo: c.correo_motivo || null, correo_donde: c.correo_donde || null,
              // MODO CORRECCION DE DATOS HEREDADOS: la ficha suelta el candado de lo facturado y
              // pinta el aviso de que esta abierto. Solo true si el servidor lo dice; ante la duda, cerrado.
              modo_correccion: c.modo_correccion === true,
              // B-870 · la versión del programa (1.XXX.XXX), para el menú. Solo si el servidor la da.
              version: typeof c.version === 'string' && c.version ? c.version : null,
              // t524 · solo si el servidor lo manda (desde t667, siempre): sin él, el objeto de siempre.
              ...(typeof c.tipo_negocio === 'string' && c.tipo_negocio ? { tipo_negocio: c.tipo_negocio } : {}) }
            const deQuien = c.clinica_id != null ? parseInt(c.clinica_id) : null
            // EL SERVIDOR ES QUIEN DICE DE QUIEN SON LOS DATOS QUE SE ESTAN VIENDO (`req.clinicaId`),
            // y se exige que conteste por la clinica en la que se cree estar: si la visita se murio
            // por el camino —o el `user` del navegador es de otra sesion— la respuesta vendria de la
            // propia y pintar su nombre encima seria la misma mentira al reves. Los tres campos van
            // juntos: el logo del dominio con el nombre del cliente es un membrete mezclado, peor que
            // cualquiera de los dos enteros.
            if (deQuien !== null && (enQue == null || deQuien === enQue) && deQuien !== delDominio) {
              v = { ...v, nombre: c.nombre || (dentro && dentro.nombre) || DEFECTO.nombre, logo: c.logo || null, color: c.color || null, color_secundario: c.color_secundario || null }
              confirmada = true
            } else if (deQuien !== null && deQuien === delDominio) {
              // EL SERVIDOR HA CONTESTADO POR LA MISMA CLÍNICA QUE RESOLVIÓ EL DOMINIO, así que la
              // marca del dominio ES la de esta sesión y no hay nada que degradar. Es el caso de una
              // visita que se quedó pegada en ese ordenador de otra sesión: quien mira ahora no es
              // personal de plataforma, su petición no lleva la cabecera, y todo está en su sitio.
              confirmada = true
            }
          }
        } catch { /* sin conexión: se decide abajo, y NUNCA hacia la marca del dominio */ }
        // ── FALLAR HACIA LA MARCA DE OTRA CLÍNICA ES FALLAR ABIERTO ────────────────────────────
        //  Aquí el `catch` decía «se queda lo que hubiera», y lo que hubiera NO es neutro: es la
        //  marca resuelta POR EL DOMINIO, o sea la del soporte. Medido: con la visita a la clínica 7
        //  viva y UN solo fallo de red, este hook servía «Clínica del soporte», su logo y su color
        //  sobre los datos del cliente — y eso se pinta en la cabecera del PDF de Informes, en el
        //  historial impreso del paciente, en las etiquetas QR del almacén, en el correo de pedido al
        //  proveedor y en la pantalla de sala de espera. Un documento con el membrete de una empresa
        //  y los datos de otra no es un documento feo: es un documento falso.
        //  Se degrada al nombre que YA se tiene de la propia visita —no hace falta preguntárselo a
        //  nadie— y a nada más. Sin nombre de visita, al del PRODUCTO, que no es de ningún cliente.
        if (!confirmada) v = { ...v, nombre: (dentro && dentro.nombre) || DEFECTO.nombre, logo: null, color: null, color_secundario: null }
        // Ya hay respuesta: se apaga la espera pase lo que pase (tambien si ha fallado — quedarse
        // «cargando» para siempre seria una pantalla congelada, que es peor que un color de mas).
        v = { ...v, cargando: false }
        guardar(v, confirmada)
      })
      // t667 · y si algo de aquí dentro lanza, la espera se apaga igual (sin guardar nada: no se ha confirmado nada).
      .catch(() => { if (vivo) setBranding((x) => (x && x.cargando ? { ...x, cargando: false } : x)) })
    return () => { vivo = false }
    // `version` está aquí para que olvidarBranding() obligue a volver a preguntar: al vaciar la
    // caché sube el número y este efecto corre otra vez. Sin él, guardar el color en Ajustes no se
    // veía hasta recargar la página.
  }, [token, version])

  return branding
}

// Para el cierre de sesión: la marca y las banderas de la clínica anterior no pueden sobrevivir a un
// cambio de usuario en la misma pantalla.
// También la usa Ajustes al guardar la marca: es lo que hace que el color nuevo se vea sin recargar.
export function olvidarBranding() {
  _memoria = null
  _memoriaToken = null
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i)
      if (k && k.startsWith('branding_cache')) localStorage.removeItem(k)
    }
  } catch { /* sin localStorage no hay nada que olvidar */ }
  // Y se avisa a las pantallas que ya están montadas. Vaciar la caché sin esto no sirve de nada:
  // su efecto depende del token, que al guardar el color NO cambia, así que ninguna volvería a
  // preguntar y el color nuevo no se vería hasta pulsar F5.
  _version++
  for (const avisar of _mirando) { try { avisar() } catch { /* una pantalla desmontada no importa */ } }
}

// ── LA SEGUNDA PUERTA, Y POR DONDE SALIA ─────────────────────────────────────────────────────────
//
//  ESTE FICHERO DECIA SER «LA UNICA PUERTA» Y NO LO ERA. La vista previa de un presupuesto y de una
//  factura (`Pacientes.jsx`, DocPreview) y dos sitios de `Ajustes.jsx` pedian el nombre, el logo y el
//  color por su cuenta:
//
//      useQuery({ queryKey: ['branding'], queryFn: () => api.get('/admin/branding')…, staleTime: 300000 })
//
//  La PETICION estaba bien —va por `api`, o sea que lleva la cabecera de la clinica visitada—, pero
//  LA CLAVE DE CACHE ERA `['branding']` A SECAS: no lleva la clinica. Y react-query no vuelve a
//  preguntar mientras la entrada este fresca. MEDIDO con el QueryClient de verdad:
//   · cebada fuera y entrando en la clinica 7 → la vista previa del presupuesto DEL CLIENTE sale con
//     el nombre, el logo y el color de la clinica del soporte, y sin ir al servidor;
//   · al reves —cebada dentro y saliendo— sale la marca del CLIENTE en un presupuesto propio.
//  Cinco minutos cada vez. Un presupuesto o una factura con el membrete de una empresa y los datos de
//  otra es exactamente el documento falso que esta pieza existe para impedir.
//
//  EL ARREGLO, EN UN SITIO: esa pregunta se hace AQUI y en ningun otro lado, y su clave lleva la
//  clinica en la que se esta. `claveDeMarca()` se exporta a proposito para que una guardia pueda
//  EJECUTARLA —dentro y fuera de una visita— en vez de leer una promesa escrita en un comentario.
export function claveDeMarca() {
  // La clinica en la que se ESTA — la visita si la hay, y si no la de la cuenta. Con la cuenta unica
  // no basta con `'propia'`: dos clinicas del mismo correo compartirian esa clave y la segunda
  // sacaria los documentos con el membrete de la primera.
  const c = _clinicaAhora()
  return ['branding', c || 'propia']
}

/**
 * LOS DATOS DE LA CLINICA PARA LOS DOCUMENTOS (nombre, logo, color, CIF, direccion, pie, clausula…).
 * Es lo que sirve `GET /admin/branding`, que el servidor contesta por `req.clinicaId`: dentro de otra
 * clinica, los suyos. Devuelve el resultado de react-query tal cual (`data`, `refetch`, …) para que
 * cada pantalla mantenga su politica: la de Ajustes NO se traga el error —si no se ha podido leer, no
 * se pinta un formulario vacio que al guardar borraria la marca—, y las vistas previas se quedan con
 * `{}` por su propio valor por defecto.
 */
export function useMarcaClinica() {
  // EL MISMO AVISO QUE `useBranding`, Y POR EL MISMO MOTIVO. Sin esta línea, la pantalla que ya está
  // montada NO se entera de que se ha cambiado de clínica: `queryClient.clear()` vacía la caché pero
  // un `useQuery` vivo conserva su último resultado hasta que React lo repinta (medido con su
  // `QueryObserver`). Y entrar en una clínica es un `navigate()`, no un F5 — igual que salir, igual
  // que el 403 que corta el acceso y el evento `storage` de otra pestaña. Con el aviso, esta pantalla
  // vuelve a pintar, `claveDeMarca()` se evalúa otra vez con la clínica nueva y se va al servidor.
  // Aquí es donde se decide con qué membrete sale la vista previa EN VIVO de un presupuesto y de una
  // factura, y las dos tarjetas de «Datos de la clínica» de Ajustes.
  useAvisoDeMarca()
  return useQuery({
    queryKey: claveDeMarca(),
    queryFn: () => api.get('/admin/branding').then(r => r.data),
    staleTime: 300000,
  })
}
